import { ActivityCardStarsRating, ViatorProviderTag } from '@care/provider-react-components';
import { seoSegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess_activitySearchResults_seoSegmentationActivity as SeoSegmentationActivity } from '@/__generated__/seoSegmentationListingPage';
import { ExternalVendors } from '@/__generated__/globalTypes';

export const setFilterURL = (
  filterVals: Record<string, any>,
  isNationalListPage: boolean,
  seoSegmentId: string
) => {
  if (Object.hasOwn(filterVals, 'subcategories')) {
    const newSegment = filterVals.subcategories.replaceAll(' ', '-').toLowerCase();
    const newSegmentForLocal = seoSegmentId.replace(/\/d\/[^/]+/, `/d/${newSegment}`);
    return isNationalListPage ? `/explore/d/${newSegment}` : newSegmentForLocal;
  }

  if (Object.hasOwn(filterVals, 'categories')) {
    const newSegment = filterVals.categories.replaceAll(' ', '-').toLowerCase();
    const newSegmentForLocal = seoSegmentId.replace(/\/d\/[^/]+/, `/d/${newSegment}`);
    return isNationalListPage ? `/explore/d/${newSegment}` : newSegmentForLocal;
  }

  return null;
};

/**
 * Takes an SEOSegmentationActivity and return a value that can be used as rating prop
 * When an activity is external and has rating the five star rating will be shown
 * Otherwise the default galore percentage rating is shown
 * @param activity SEOSegmentationActivity
 * @returns rating prop for the ActivityCard
 */
export const getActivityCardRating = (activity: SeoSegmentationActivity) => {
  const { totalReviews, avarageRating } = activity.externalVendor?.info?.rating ?? {};
  if (avarageRating) {
    return (
      <ActivityCardStarsRating
        // viator brand color which is not part of the care palette
        iconProps={{ color: 'rgb(69, 183, 137);' }}
        rating={avarageRating}
        totalReviews={totalReviews ?? undefined}
      />
    );
  }

  return activity.reviews?.positiveReviews ?? 0;
};

/**
 * Takes an SEOSegmentationActivity and return a value that can be used as organizationName prop
 * When an activity is external and provided by Viator the custom viator tag is shown
 * Otherwise the default galore provider name is shown
 * @param activity SEOSegmentationActivity
 * @returns organizationName prop for the ActivityCard
 */
export const getActivityCardProvider = (activity: SeoSegmentationActivity) => {
  const { vendorName } = activity.externalVendor ?? {};
  if (vendorName === ExternalVendors.VIATOR) return <ViatorProviderTag />;
  return activity.providerName ?? '';
};

/**
 * Takes an SEOSegmentationActivity and return a value that can be used as imageUrls prop
 * When an activity is external and has some image the image will be shown
 * Otherwise the images provided by galore are shown
 * @param activity SEOSegmentationActivity
 * @returns imageUrls prop for the ActivityCard
 */
export const getActivityCardImages = (activity: SeoSegmentationActivity) => {
  const { photoUrl } = activity.externalVendor?.info ?? {};
  return photoUrl ? [photoUrl] : activity.photos;
};

/**
 * Takes an SEOSegmentationActivity and return a value that can be used as a href prop
 * When an activity is external and has an url the activity card will navigate to this link when clicked
 * Otherwise the galore url is used
 * @param activity SEOSegmentationActivity
 * @returns linkProps.href prop for the ActivityCard
 */
export const getActivityCardUrl = (activity: SeoSegmentationActivity) => {
  const { activityUrl: externalUrl } = activity.externalVendor?.info ?? {};

  if (externalUrl) return externalUrl;
  if (!activity.url) return undefined;
  return activity.url;
};
